import './GridManager.css';
import {
	IonPage, IonHeader, IonToolbar, IonButtons, IonBackButton, IonTitle, IonContent, IonRow, IonLoading,
	IonButton, IonIcon, IonCard, IonText, IonCardHeader, IonCardTitle, IonCardSubtitle, IonCardContent, IonList, useIonAlert
} from '@ionic/react';
import {arrowBack, cloudUploadOutline, downloadOutline} from 'ionicons/icons';
import React from 'react';
import {useMutation} from "@apollo/client";
import {PARSE_GRID_FILES, LOAD_NEW_GRIDS} from "../schemas/gridmanager";

import {
	ParseGridFilesRequest,
	ParseGridFilesResponse,
	LoadNewGridsResponse,
	AmsGridUnparsed,
	AmsGridPropertyKeyValuePair
} from "@backend/types/gridmanager"



enum GridManagerState {
	UPLOAD_FILES = 1,
	REVIEW_GRIDS = 2
}

enum ETC {
	NONE = '',
	ENTOUCH = 'ENTOUCH',
	ASSIST = 'ASSIST',
}

function escapeHtml(str: any): string {
	return String(str)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#x27;')
		.replace(/`/g, '&#x60;');
}

// when accessed via an embedded iframe, the URL passes 'client' to indicate what ETC the user is currently on
function getEtcFromUrlParameters(): ETC {
	const currentClient = new URLSearchParams(window.location.search).get("client");
	if (currentClient === 'ent') {
		return ETC.ENTOUCH;
	} else if (currentClient === 'aw') {
		return ETC.ASSIST;
	} else {
		if (currentClient) {
			console.warn(`Client URL parameter passed in was '${currentClient}' instead of expected values [null, 'ent', 'aw']`);
		}
		return ETC.NONE;
	}
}

// if a tab name is included in the input text, focus that tab - this can highlight tabs referred to by error messages

const GridManager: React.FC<any> = ({embedded = false}) => {
	const DEFAULT_LOADING_MESSAGE: string = 'Please wait...';
	const etc: ETC = getEtcFromUrlParameters();

	const [gridsToReview, setGridsToReview] = React.useState<any[]>([]);
	const [activeTabIndex, setActiveTabIndex] = React.useState<number>(0);
	const [flashUnviewedTabs, setFlashUnviewedTabs] = React.useState<boolean>(false);
	const [presentAlert] = useIonAlert();
	const [tabsViewed, setTabsViewed] = React.useState<Record<number, boolean>>({});
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [loadingMessage, setLoadingMessage] = React.useState<string>(DEFAULT_LOADING_MESSAGE);
	const [gridManagerState, setGridManagerState] = React.useState<GridManagerState>(GridManagerState.UPLOAD_FILES);

	const [parseGridFilesMutation] = useMutation(PARSE_GRID_FILES);
	const [loadNewGridsMutation] = useMutation(LOAD_NEW_GRIDS);

	// ApolloError needs to be stringified, otherwise it will not include details when using console.error
	const logPossibleApolloError = (error: any) => {
		if (error?.graphQLErrors !== undefined) {
			console.error(JSON.stringify(error, null, 2));
		} else {
			console.error(error);
		}
	}

	// if a tab name is included in the input text, focus that tab - this can highlight tabs referred to by error messages
	function tryFocusTab(potentialTabNameText: string, currentGrids: AmsGridUnparsed[]): void {
		if (!potentialTabNameText) return;
		const matchIndex = currentGrids.findIndex((g: any) => {
			const name = g.properties.find((p: AmsGridPropertyKeyValuePair) => p.key === 'Name')?.value ?? '';
			return potentialTabNameText.includes(name);
		});
		if (matchIndex !== -1) setActiveTabIndex(matchIndex);
	}

	const showConfirm = async (message: string, onConfirm: () => Promise<void>): Promise<void> => {
		presentAlert({
			header: 'Confirm',
			message,
			buttons: [
				{text: 'No', role: 'cancel'},
				{text: 'Yes', role: 'confirm'}
			],
			onDidDismiss: async (ev: CustomEvent) => {
				if (ev.detail.role === 'confirm') {
					await onConfirm();
				}
			}
		});
	}

	const showLoadMessage = (message: string | boolean) => {
		if (message && typeof message === 'string') {
			setLoadingMessage(message);
			setIsLoading(false);
			setIsLoading(true);
		} else {
			setLoadingMessage(DEFAULT_LOADING_MESSAGE);
			setIsLoading(false);
		}
	}

	// the animation plays once for elements where tab_viewed=0, so setting this to null then back will replay it
	const showError = async (errorMessage: string): Promise<void> => {
		showLoadMessage(false);
		const plainMessage = errorMessage.replace(/<br\s*\/?>/gi, '\n').replace(/<\/?[^>]+(>|$)/g, '');
		presentAlert({
			header: 'Error',
			message: plainMessage,
			buttons: ['OK']
		});
	}

	const onFilesSelected = async ({target: {validity, files}}: any) => {
		if (!files.length) {
			return;
		}

		showLoadMessage('Uploading files...');
		try {
			if (validity.valid) {
				await parseGridFiles(files);
			} else {
				showError('Invalid input');
				showLoadMessage(false);
			}
		} catch (error) {
			logPossibleApolloError(error);
			showError(`Failed to parse loaded files:\n\n${escapeHtml(error)}`);
			showLoadMessage(false);
		}
	}

	const parseGridFiles = async (files: File[]) => {
		const parseGridFilesRequest: ParseGridFilesRequest = {files};
		const parseGridFilesResult = await parseGridFilesMutation({variables: parseGridFilesRequest});
		const parseGridFilesResponse: ParseGridFilesResponse = parseGridFilesResult?.data?.parseGridFiles;
		const {success, error, grids} = parseGridFilesResponse;

		if (!success) {
			return showError(
				`Error when loading template files:<br/><br/>
				 <i>${escapeHtml(error)}</i><br/><br/>
				 Please ensure you are using the latest version of the grid template. 
				 The latest version can be retrieved from the "download template" button.`
			);
		}

		setTabsViewed({});

		// initialize tabs viewed — first tab is auto-viewed
		grids.forEach((_: any, i: number) => {
            setTabsViewed(tabsViewed => ({
                ...tabsViewed,
                [i]: (i === 0)
            }));
		});

		setGridsToReview(grids);
		setActiveTabIndex(0);
		setGridManagerState(GridManagerState.REVIEW_GRIDS);
		showLoadMessage(false);
	}

    const loadGridIntoAms = async (commit: boolean) => {
        let confirmText;

        if (commit) {
            const tabsNotViewedCount = Object.entries(tabsViewed).filter(tab => !tab[1]).length;
            const tabsViewedCount = Object.entries(tabsViewed).filter(tab => tab[1]).length;
            if (tabsNotViewedCount > 0) {
                setFlashUnviewedTabs(true);
                setTimeout(() => setFlashUnviewedTabs(false), 300);
                return await showError(
                    `Please review each grid before loading them into AMS. 
						You have viewed ${tabsViewedCount} out of ${tabsViewedCount + tabsNotViewedCount} tabs.`
                );
            }
            confirmText = `Load ${tabsViewedCount} grids into AMS now?`;
        } else {
            confirmText = `Validate grids?`;
        }

        await showConfirm(confirmText, async () => {
            try {
                showLoadMessage('Validating grids...');

                // build up the list of grids to send to the loadNewGrids call
                const amsGridsToSend: AmsGridUnparsed[] = [];
                for (let amsGridIndex = 0; amsGridIndex < gridsToReview.length; amsGridIndex++) {
                    const currentAmsGrid = gridsToReview[amsGridIndex];
                    const amsGrid: AmsGridUnparsed = ({
                        properties: [],
                        tiers: []
                    });

                    // populate amsGrid.properties
                    for (let i = 0; i < currentAmsGrid.properties.length; i++) {
                        const currentProperty = currentAmsGrid.properties[i];
                        const property = {
                            key: currentProperty.key,
                            value: currentProperty.value
                        };
                        amsGrid.properties.push(property);
                    }

                    // populate amsGrid.tiers
                    for (let i = 0; i < currentAmsGrid.tiers.length; i++) {
                        const currentTier = currentAmsGrid.tiers[i];
                        const tier = {
                            enrollments: Number(currentTier.enrollments),
                            earnings: Number(currentTier.earnings),
                            retention: Number(currentTier.retention),
                            second_usage: Number(currentTier.second_usage),
                            invoice_submission: Number(currentTier.invoice_submission),
                            valid_email: Number(currentTier.valid_email),
                            tier: Number(`${currentTier.tier}`.split(' ')[1])
                        }
                        amsGrid.tiers.push(tier);
                    }

                    amsGridsToSend.push(amsGrid);
                }

                // load the grids without committing to validate them
                let result = await loadNewGridsMutation({variables: {grids: amsGridsToSend, noCommit: true, etc}});
                let response: LoadNewGridsResponse = result?.data?.loadNewGrids;

                // if validation was successful, and we want to commit, run the call again without the final rollback
                if (commit && response?.success) {
                    showLoadMessage('Loading grids...');
                    result = await loadNewGridsMutation({variables: {grids: amsGridsToSend, noCommit: false, etc}});
                    response = result?.data?.loadNewGrids;
                }

                // display any errors if applicable
                if (!response) {
                    return showError("An unexpected error has occurred while loading the grids into AMS");
                }
                if (response.error) {
                    tryFocusTab(response.error, gridsToReview);
                    return showError(`Failed to load grids into AMS:\n\n${escapeHtml(response.error)}`);
                }
                if (!response.success) {
                    return showError(`Load failed unexpectedly`);
                }

                // load was a success
                showLoadMessage(false);
                const loadedGrids = response?.grids;
                if (!loadedGrids) {
                    return showError(`No grids loaded from response`);
                }

                let successMessage: string;
                if (commit) {
                    successMessage = `Loaded new grid into AMS:\n\n${loadedGrids.map((grid: any) => escapeHtml(grid.properties.name)).join('\n')}`;
                } else {
                    successMessage = `The grids have been validated and can be loaded into AMS.`;
                }
                presentAlert({
                    header: 'Success',
                    message: successMessage,
                    buttons: ['OK'],
                    onDidDismiss: () => {
                        if (commit) {
                            setGridManagerState(GridManagerState.UPLOAD_FILES);
                        }
                    }
                });
            } catch (error) {
                logPossibleApolloError(error);
                return showError(`An unexpected error has occurred: ${escapeHtml(error)}`);
            }
        });
    }

	return (
		<IonPage>
			{!embedded &&
				<IonHeader>
					<IonToolbar>
						<IonButtons slot="secondary">
							<IonBackButton icon={arrowBack} text="" className="custom-back"/>
						</IonButtons>
						<IonTitle>Grid Manager</IonTitle>
					</IonToolbar>
				</IonHeader>
			}
			<IonLoading isOpen={isLoading} message={loadingMessage}/>
			<IonContent fullscreen className="grid-manager">
				{/* Uploading files UI */}
				{(gridManagerState === GridManagerState.UPLOAD_FILES) &&
					<div className="grid-manager-details">
						<IonCard className="grid-manager-primary-card">
							<IonCardHeader>
								<IonCardTitle>AMS Grid Loader</IonCardTitle>
								<IonCardSubtitle>Upload new grids to AMS</IonCardSubtitle>
							</IonCardHeader>
							<IonCardContent>
								<IonText>
									<p>
										This tool will allow you to upload new grids on AMS. After loading, they will be made available
										to select using the Admin Edit Tool. These grids can be assigned directly to enrollment reps, or
										made available for masters and managers to select during onboarding. By default, these grids
										will be loaded as active grids that are not available to any master/manager and have no users
										assigned to them.
									</p>
									<p>
										To load a grid, download the newest version of the template using the <i>"Download
										Template"</i> button, and fill it out accordingly. After you have your files ready, you can upload
										them using the <i>"Upload Files"</i> button. You can select up to 5 grids to load at once. After
										uploading, you will be given a chance to review and validate each of the grids before
										submitting. <b>Grids will not be made available until you have reviewed the files and submitted them
										after uploading the files.</b>
									</p>
									<p>
										Please ensure that the grids are correct before loading. If an incorrect grid is loaded into AMS,
										correct the template and re-upload with a newer version number. Deactivating grids will require
										submitting a ticket to the development team.
									</p>
								</IonText>
								<IonList>
									<IonRow>
										{/* this event is so both the label and the button trigger an upload event, without doubling up */}
										<IonButton id="fileButton" fill="solid" size="large" color="primary" onClick={(e) => {
											e.stopPropagation();
											(document as any).getElementById('file').click(false);
											return false;
										}}>
											<IonIcon icon={cloudUploadOutline} slot="start"></IonIcon>
											<input id="file" multiple={true} hidden type="file"
											       accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
											       onChange={onFilesSelected}
											       onClick={(event) => {
												       event.currentTarget.value = ''
											       }}/>
											<span>Upload Files</span>
										</IonButton>
									</IonRow>
									<IonRow>
										<IonButton fill="solid" size="large" color="secondary"
										           href="https://paygo-ams-public.s3-us-west-2.amazonaws.com/PayGridTemplate_Clean.xlsx">
											<IonIcon icon={downloadOutline} slot="start"/>
											Download Template
										</IonButton>
									</IonRow>
								</IonList>
							</IonCardContent>
						</IonCard>
					</div>
				}
				{/* Reviewing grids UI */}
				{(gridManagerState === GridManagerState.REVIEW_GRIDS) && gridsToReview.length > 0 && (
					<div className="grid-review-container">
						<IonToolbar>
							<IonButtons slot="start">
								<IonButton fill="solid" color="danger" onClick={() => {
									setGridManagerState(GridManagerState.UPLOAD_FILES);
									setGridsToReview([]);
								}}>Cancel</IonButton>
							</IonButtons>
							<IonTitle>Please review all grids before submitting</IonTitle>
							<IonButtons slot="end">
								<IonButton fill="solid" color="medium" onClick={() => loadGridIntoAms(false)}>Validate</IonButton>
								<IonButton fill="solid" color="primary" onClick={() => loadGridIntoAms(true)}>Submit</IonButton>
							</IonButtons>
						</IonToolbar>

						<div className="grid-tab-bar">
							{gridsToReview.map((grid: any, i: number) => {
								const name = grid.properties.find((p: any) => p.key === 'Name')?.value ?? `Grid ${i + 1}`;
								const viewed = tabsViewed[i];
								return (
									<button key={`gridButton${name}`}
											className={['grid-tab', activeTabIndex === i ? 'active' : '', !viewed ? 'unviewed' : 'viewed', flashUnviewedTabs && !viewed ? 'unviewed-flash' : ''].join(' ').trim()}											onClick={() => {
												setActiveTabIndex(i);
												setTabsViewed(tabsViewed => ({...tabsViewed, [i]: true}));
											}}
									>{name}</button>
								);
							})}
						</div>

						{(() => {
							const grid = gridsToReview[activeTabIndex];
							if (!grid) {
								return null;
							}
							return (
								<div className="grid-content">
									<table className="grid-properties-table">
										<thead><tr><th colSpan={2}>Grid Properties</th></tr></thead>
										<tbody>
										{grid.properties.map((p: any) => (
											<tr key={p.key}><td>{p.key}</td><td>{p.value}</td></tr>
										))}
										</tbody>
									</table>
									<div className="grid-tiers-scroll">
										<table className="grid-tiers-table">
											<thead>
											<tr>
												<th>Tier</th><th>Active Customers</th><th>Earnings</th>
												<th>Retention</th><th>Second Usage</th>
												<th>Valid Email Address</th><th>Invoice Submission</th>
											</tr>
											</thead>
											<tbody>
											{grid.tiers.map((tier: any, i: number) => (
												<tr key={i}>
													<td>{tier.tier ? String(tier.tier).split(' ')[1] : tier.tier}</td>
													<td>{tier.enrollments}</td>
													<td>{Number(tier.earnings).toLocaleString('en-US', {style: 'currency', currency: 'USD'})}</td>
													<td>{Number(tier.retention).toLocaleString(undefined, {minimumFractionDigits: 2}) + '%'}</td>
													<td>{Number(tier.second_usage).toLocaleString(undefined, {minimumFractionDigits: 2}) + '%'}</td>
													<td>{Number(tier.valid_email).toLocaleString(undefined, {minimumFractionDigits: 2}) + '%'}</td>
													<td>{Number(tier.invoice_submission).toLocaleString(undefined, {minimumFractionDigits: 2}) + '%'}</td>
												</tr>
											))}
											</tbody>
										</table>
									</div>
								</div>
							);
						})()}
					</div>
				)}
			</IonContent>
		</IonPage>
	);
};

export default GridManager;